import { ArrowLeft } from "lucide-react";
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { FaUpload, FaSpinner, FaEye } from "react-icons/fa";
import { toast } from "react-toastify";
import API from "../../config/config";
import PanCardPreview from "../../components/Driver/PanCardPreview";
import { compareNames } from "../../components/Driver/compareNames"; // adjust path

const VerifyPancard = () => {
  const [panNumber, setPanNumber] = useState("");
  const [fullName, setFullName] = useState("");
  const [panFrontFile, setPanFrontFile] = useState(null);
  const [existingPanFrontUrl, setExistingPanFrontUrl] = useState(null);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showFullImage, setShowFullImage] = useState(false);
  const token = localStorage.getItem("userPublicToken");
  const navigate = useNavigate();

  const panFrontPreview = useMemo(
    () => panFrontFile && URL.createObjectURL(panFrontFile),
    [panFrontFile]
  );

  useEffect(() => {
    const fetchPanInfo = async () => {
      try {
        const response = await fetch(API.ADMIN.USERDASHBOARD, {
          headers: {
            Authorization: "Bearer " + token,
          },
        });

        if (response.ok) {
          const data = await response.json();
          console.log(data);
          const panDetails = data.data?.panDetails;

          if (panDetails) {
            setPanNumber(panDetails.pan_number || "");
            setFullName(panDetails.user_full_name || "");
          }

          if (panDetails?.panfront) {
            setExistingPanFrontUrl(panDetails.panfront || null);
          }
        }
      } catch (error) {
        console.error("Failed to load PAN details", error);
      }
    };

    fetchPanInfo();
  }, [token]);

  const handleNextStep = () => {
    if (!panNumber || !fullName) {
      alert("Please fill in all required fields.");
      return;
    }

    const dlName = localStorage.getItem("dlName") || "";

    if (!compareNames(fullName, dlName)) {
      alert(
        "The name on PAN Card does not match the name on your Driving License. Please enter the correct name."
      );
      return;
    }

    setStep(2);
  };

  const uploadPanFile = async (file, side) => {
    const headers = new Headers();
    headers.append("Authorization", "Bearer " + token);

    const formdata = new FormData();
    formdata.append("type", "pancard");
    formdata.append("documentFor", "Driver");
    formdata.append("side", side);
    formdata.append("file", file, file.name);

    const response = await fetch(API.ADMIN.DOCUMENT_UPLOAD, {
      method: "POST",
      headers,
      body: formdata,
    });

    if (!response.ok) throw new Error(`${side} PAN upload failed.`);
    const data = await response.json();
    return data.document?.documentUrl || null;
  };

  const handleSubmit = async () => {
    if (!panFrontFile && !existingPanFrontUrl) {
      alert("Please upload both PAN Image");
      return;
    }

    setLoading(true);
    try {
      const frontUrl = panFrontFile
        ? await uploadPanFile(panFrontFile, "front")
        : existingPanFrontUrl;

      const payload = {
        panNumber,
        holderName: fullName,
        consent: "Y",
        consentText:
          "I hereby declare my consent for fetching my information via ZOOP API",
        taskId: "baad2be0-e9c3-4217-9f4f-3c7808f317ba",
        panfront: frontUrl,
      };

      const response = await fetch(API.ADMIN.VERIFY_PAN_CARD, {
        method: "POST",
        headers: {
          Authorization: "Bearer " + token,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();
      console.log("PAN Verification Result:", result);
      console.log("Result success:", result.success);
      console.log("Result message:", result.message);

      if (!response.ok) {
        console.log("Response not OK, status:", response.status);
        throw new Error(result.message || "PAN verification failed.");
      }

      console.log("Response OK, processing result...");

      // Check for specific in-progress message
      if (
        result.success === true &&
        result.message &&
        (result.message ===
          "Verification failed, but status saved as InProgress" ||
          result.message.includes("InProgress") ||
          result.message.includes("in progress"))
      ) {
        // ✅ Specific message matched — treat as "in progress"
        console.log("In progress case detected");
        toast.info("PAN card verification is in progress.");
        setTimeout(() => navigate("/user-dashboard"), 1500);
      } else if (result.success === true) {
        // ✅ Normal success case
        console.log("Success case detected");
        toast.success("PAN card submitted successfully!");
        setTimeout(() => navigate("/user-dashboard"), 1500);
      } else {
        // ❌ Fallback handling - treat as in progress if no clear success
        console.log("Fallback case - treating as in progress");
        toast.info("PAN card verification is in progress.");
        setTimeout(() => navigate("/user-dashboard"), 1500);
      }
    } catch (error) {
      console.error(error);
      toast.error(error.message || "PAN verification failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto my-10 p-6 bg-white rounded-lg shadow-md">
      {step === 1 && (
        <>
          <div className="flex items-center mb-6">
            <button
              onClick={() => navigate("/user-dashboard")}
              className="mr-3 text-gray-700 hover:text-indigo-600"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <h2 className="text-xl font-semibold text-gray-800">
              Enter PAN Card Details
            </h2>
          </div>
          <form className="space-y-4" onSubmit={(e) => e.preventDefault()}>
            <div>
              <label className="block font-medium">PAN Number</label>
              <input
                type="text"
                value={panNumber}
                onChange={(e) => {
                  const input = e.target.value.toUpperCase();

                  // Allow only uppercase letters and digits, and only up to 10 characters
                  if (/^[A-Z0-9]{0,10}$/.test(input)) {
                    setPanNumber(input);
                  }
                }}
                className="w-full border px-3 py-2 rounded border-gray-300"
                placeholder="ABCDE1234F"
              />
            </div>
            <div>
              <label className="block font-medium">Full Name</label>
              <input
                type="text"
                value={fullName}
                onChange={(e) => {
                  const value = e.target.value;
                  // Allow only letters (upper/lowercase) and spaces
                  if (/^[A-Za-z\s]*$/.test(value)) {
                    setFullName(value);
                  }
                }}
                className="w-full border px-3 py-2 rounded border-gray-300"
                placeholder="Full Name on PAN"
              />
            </div>
            <button
              className="w-full bg-indigo-600 text-white py-2 rounded"
              onClick={handleNextStep}
            >
              Next
            </button>
          </form>
        </>
      )}

      {step === 2 && (
        <>
          <div className="flex items-center mb-6">
            <button
              onClick={() => setStep(1)}
              className="mr-3 text-gray-700 hover:text-indigo-600"
              type="button"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <h2 className="text-xl font-semibold text-gray-800">
              Upload PAN Card Image
            </h2>
          </div>
          <div className="space-y-6">
            <div className="border p-4 rounded shadow-sm">
              <label className="block text-sm font-semibold mb-1">
                Upload Front Side of PAN Card
              </label>
              <p className="text-xs text-gray-600 mb-2">
                Upload a clear picture of the front side of your PAN card.
              </p>

              {/* Only show file input if no existing image and no selected file */}
              {!existingPanFrontUrl && !panFrontFile && (
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    setPanFrontFile(e.target.files[0]);
                    setExistingPanFrontUrl(null);
                  }}
                />
              )}

              {(panFrontPreview || existingPanFrontUrl) && (
                <div className="mt-3">
                  <img
                    src={panFrontPreview || existingPanFrontUrl}
                    alt="PAN Front Preview"
                    className="w-full rounded border"
                  />
                  <div className="flex gap-4 mt-4 justify-center">
                    {existingPanFrontUrl && !panFrontFile && (
                      <button
                        onClick={() => setShowFullImage(true)}
                        className="flex items-center gap-2 text-blue-600 hover:text-blue-800 text-sm"
                      >
                        <FaEye />
                        View Full Image
                      </button>
                    )}
                    <label className="cursor-pointer flex gap-2 items-center text-sm text-blue-600 hover:text-blue-800">
                      <FaUpload />
                      Change
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          setPanFrontFile(e.target.files[0]);
                          setExistingPanFrontUrl(null);
                        }}
                      />
                    </label>
                  </div>
                </div>
              )}
            </div>

            <button
              className={`w-full bg-indigo-600 text-white py-2 rounded ${
                !panFrontFile && !existingPanFrontUrl
                  ? "opacity-50 cursor-not-allowed"
                  : ""
              }`}
              onClick={() => setStep(3)}
              disabled={!panFrontFile && !existingPanFrontUrl}
            >
              Next: Preview
            </button>
          </div>
        </>
      )}

      {step === 3 && (
        <>
          <div className="flex items-center mb-4">
            <button
              onClick={() => setStep(2)}
              type="button"
              className="mr-3 text-gray-700 hover:text-indigo-600"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <h2 className="text-xl font-semibold text-gray-800">Preview</h2>
          </div>
          <PanCardPreview
            panNumber={panNumber}
            fullName={fullName}
            panFrontFile={panFrontFile}
            panFrontPreview={panFrontPreview}
            existingPanFrontUrl={existingPanFrontUrl}
          />
          <button
            className={`w-full mt-4 bg-green-600 text-white py-2 rounded ${
              loading ? "opacity-70 cursor-not-allowed" : ""
            }`}
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? (
              <div className="flex items-center justify-center">
                <FaSpinner className="animate-spin mr-2" />
                Submitting...
              </div>
            ) : (
              "Confirm & Submit"
            )}
          </button>
        </>
      )}

      {/* Full Image Modal */}
      {showFullImage && existingPanFrontUrl && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="relative max-w-4xl max-h-full p-4">
            <img
              src={existingPanFrontUrl}
              alt="PAN Card Full View"
              className="max-w-full max-h-full object-contain rounded-lg"
            />
            <button
              onClick={() => setShowFullImage(false)}
              className="absolute top-2 right-2 bg-white text-gray-800 rounded-full w-8 h-8 flex items-center justify-center hover:bg-gray-100"
            >
              ×
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default VerifyPancard;
